import React from 'react';
import './ScreenDataTable.css';

interface PageOption {
    limit: number;
}

const DEFAULT_OPTIONS: PageOption[] = [
    {limit: 20},
    {limit: 50},
    {limit: 100},
];

interface ScreenDataTablePaginationProps {
    totalRecords: number;
    pageSize: number;
    canGoPrev: boolean;
    canGoNext: boolean;
    onPrevPage: () => void;
    onNextPage: () => void;
    onPageSizeChange?: (size: number) => void;
    screenId?: string;
    pageSizeOptions?: PageOption[];
    position?: 'top' | 'bottom';
    /** 0-based index of the current page (afterIdHistory.length). */
    pageIndex?: number;
    /** Number of rows actually shown on the current page. */
    rowsOnPage?: number;
}

export const ScreenDataTablePagination: React.FC<ScreenDataTablePaginationProps> = ({
    totalRecords,
    pageSize,
    canGoPrev,
    canGoNext,
    onPrevPage,
    onNextPage,
    onPageSizeChange,
    pageSizeOptions,
    position = 'bottom',
    pageIndex,
    rowsOnPage,
    screenId,
}) => {
    const opts = pageSizeOptions && pageSizeOptions.length > 0 ? pageSizeOptions : DEFAULT_OPTIONS;
    if (totalRecords === 0) return null;

    // Actual range shown on this page ("101 – 200 z 217"). Previous pages are
    // always full (next is only enabled on a full page), so index × pageSize
    // is exact. Falls back to the plain total when the caller doesn't pass it.
    const hasRange = pageIndex !== undefined && rowsOnPage !== undefined && rowsOnPage > 0;
    const rangeFrom = hasRange ? pageIndex! * pageSize + 1 : null;
    const rangeTo = hasRange ? pageIndex! * pageSize + rowsOnPage! : null;

    // On a phone the footer breaks into two rows: the summary, then the
    // prev/next pair stretched across the full width so each is a real
    // thumb target. The summary also drops its filler words there, keeping
    // "1 – 20 z 28" on one line instead of wrapping into a four-line column.
    return (
        <div className={`card-footer screen-pagination d-flex flex-wrap align-items-center gap-2${position === 'top' ? ' border-top-0 border-bottom' : ''}`}>
            <div className="d-flex align-items-center flex-wrap gap-2">
                <span className="text-secondary">Zobrazujem</span>
                {onPageSizeChange ? (
                    <select
                        data-e2e={`pagesize:${screenId}`}
                        className="form-select form-select-sm"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        style={{ width: 'auto' }}
                        disabled={opts.length <= 1}
                    >
                        {opts.map((opt) => (
                            <option key={opt.limit} value={opt.limit}>{opt.limit}</option>
                        ))}
                    </select>
                ) : (
                    <strong>{pageSize}</strong>
                )}
                <span className="text-secondary text-nowrap">
                    {hasRange ? (
                        <><span className="d-none d-sm-inline">na stranu &middot; </span><strong>{rangeFrom === rangeTo ? rangeTo : `${rangeFrom} – ${rangeTo}`}</strong> z <strong>{totalRecords}</strong><span className="d-none d-sm-inline"> záznamov</span></>
                    ) : (
                        <>z <strong>{totalRecords}</strong><span className="d-none d-sm-inline"> záznamov</span></>
                    )}
                </span>
            </div>
            {(canGoPrev || canGoNext) && (
                <ul className="pagination screen-pagination__nav m-0 ms-sm-auto">
                    <li className={`page-item ${!canGoPrev ? 'disabled' : ''}`}>
                        <button
                            type="button"
                            data-e2e={`prev:${screenId}`}
                            className="page-link d-flex align-items-center"
                            onClick={onPrevPage}
                            disabled={!canGoPrev}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon me-1">
                                <path d="M15 6l-6 6l6 6"></path>
                            </svg>
                            Predošlá
                        </button>
                    </li>
                    <li className={`page-item ${!canGoNext ? 'disabled' : ''}`}>
                        <button
                            type="button"
                            data-e2e={`next:${screenId}`}
                            className="page-link d-flex align-items-center"
                            onClick={onNextPage}
                            disabled={!canGoNext}
                        >
                            Ďalšia
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon ms-1">
                                <path d="M9 6l6 6l-6 6"></path>
                            </svg>
                        </button>
                    </li>
                </ul>
            )}
        </div>
    );
};
